import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    DialogOpenChangeData,
    DialogOpenChangeEvent,
    DialogOpenChangeEventHandler,
} from '@fluentui/react-dialog';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Dropdown,
    Field,
    Option,
} from '@fluentui/react-components';
import {
    DatePicker,
    DatePickerValidationResultData,
    defaultDatePickerErrorStrings,
} from '@fluentui/react-datepicker-compat';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useNewReportDialogActionStyles } from './NewReportDialogAction.styles';
import { addDays } from '@fluentui/react-calendar-compat';
import { ReportParametersInputModel, ReportType } from '../../../model';
import { useAppContext } from '../../../model';
import {
    getConnectToTeamsForReporting,
    selectHelp,
} from '../../../store/features/help/helpSlice';
import { useAppDispatch, useSelector } from '../../../store/hooks';
import {
    fetchCanAccessGroundTeamRatingsReport,
    selectCanAccessGroundTeamRatingsReport,
    fetchCanViewAllGroundTeamRatingsReport,
    selectCanViewAllGroundTeamRatingsReport,
} from '../../../store/features/reporting/reportingSlice';
import { useGroundTeamReportAvailability } from '../../../hooks/useGroundTeamReportAvailability';

interface NewReportDialogActionProps {
    open: boolean;
    onOpenChange?: DialogOpenChangeEventHandler;
    onSubmit: (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        data: ReportParametersInputModel
    ) => void;
}

const today = new Date();
const fromMaxDate = addDays(today, 1);
const toMaxDate = addDays(today, 1);

const onFormatDate = (date?: Date): string => {
    return !date
        ? ''
        : `${date?.getDate()}/${date?.getMonth() + 1}/${date?.getFullYear()}`;
};

const baseReportTypes: readonly ReportType[] = [
    ReportType.ConversationReport,
    ReportType.BagMessagesReport,
    ReportType.FailedConnectionsReport,
    ReportType.MisconnectedCustomersReport,
    ReportType.CheckInReport,
    ReportType.CustomerMessagesReport,
];

const isConversationReport = (reportType: ReportType | undefined) =>
    reportType === ReportType.GroundConversationReport ||
    reportType === ReportType.ConversationReport;

const acceptsTeamFilter = (reportType: ReportType | undefined) =>
    reportType === ReportType.ConversationReport ||
    reportType === ReportType.GroundTeamRatingsReport;

const NewReportDialogAction = ({
    open,
    onOpenChange,
    onSubmit,
}: NewReportDialogActionProps) => {
    const { t } = useTranslation();
    const styles = useNewReportDialogActionStyles();

    const [reportType, setReportType] = useState<ReportType | undefined>();
    const [fromDate, setFromDate] = useState<Date | undefined | null>();
    const [toDate, setToDate] = useState<Date | undefined | null>();
    const [selectedTeamId, setSelectedTeamId] = useState("");
    const [includeCheckIns, setIncludeCheckIns] = useState(false);
    const [startDateError, setStartDateError] = useState<DatePickerValidationResultData['error']>(undefined);
    const [endDateError, setEndDateError] = useState<DatePickerValidationResultData['error']>(undefined);

    const { teamsContext } = useAppContext();
    const installationTeamId = teamsContext?.team?.groupId ?? '';
    const dispatch = useAppDispatch();

    const { canAccess, status: canAccessStatus } = useSelector(
        selectCanAccessGroundTeamRatingsReport
    );
    const { canViewAllTeams, status: canViewAllTeamsStatus } = useSelector(
        selectCanViewAllGroundTeamRatingsReport
    );
    const { reportingTeams, reportingTeamsStatus } = useSelector(selectHelp);

    // Check if user is lead or admin to view ratings reports
    useEffect(() => {
        if (open && canAccessStatus === 'idle' && canAccess === null) {
            dispatch(fetchCanAccessGroundTeamRatingsReport());
        }
    }, [open, canAccessStatus, canAccess, dispatch]);
    // Check if user is admin and can view all ground team ratings from all teams
    useEffect(() => {
        if (
            open &&
            canAccess === true &&
            canViewAllTeamsStatus === 'idle' &&
            canViewAllTeams === null
        ) {
            dispatch(fetchCanViewAllGroundTeamRatingsReport());
        }
    }, [open, canAccess, canViewAllTeamsStatus, canViewAllTeams, dispatch]);
    // Get all teams for reporting (includes both contactable and non-contactable Help teams)
    useEffect(() => {
        dispatch(getConnectToTeamsForReporting());
    }, [dispatch]);
    const reportTypes = useMemo(
        () =>
            canAccess
                ? [...baseReportTypes, ReportType.GroundTeamRatingsReport]
                : baseReportTypes,
        [canAccess]
    );

    const {
        teamsWithAll,
        shouldRestrictGroundTeamRatingsReport,
        hasValidSelectedTeam,
        isRestrictedOption,
        getDefaultGroundTeamId,
        allTeamsOption,
    } = useGroundTeamReportAvailability({
        reportType,
        reportingTeams,
        reportingTeamsStatus,
        installationTeamId,
        canViewAllTeams,
    });

    const selectedTeamName = useMemo(
        () =>
            teamsWithAll.find((team) => team.id === selectedTeamId)
                ?.name || allTeamsOption.name,
        [teamsWithAll, selectedTeamId, allTeamsOption]
    );

    const maxRangeDays = useMemo(() => {
        return selectedTeamId === allTeamsOption.id ? 184 : 365;
    }, [selectedTeamId, allTeamsOption.id])

    const isGroundRatingsLocked =
        reportType === ReportType.GroundTeamRatingsReport &&
        canViewAllTeams === false;

    const isFormDisabled =
        reportingTeamsStatus === 'loading' ||
        shouldRestrictGroundTeamRatingsReport;

    const isTeamSelectionLocked =
        isGroundRatingsLocked || shouldRestrictGroundTeamRatingsReport;

    // Validation
    const isRangeValid = useCallback(() => {
        if (fromDate && toDate) {
            const diffDays =
                (toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24);
            return diffDays >= 0 && diffDays <= maxRangeDays;
        }
        return true;
    }, [fromDate, toDate, maxRangeDays]);

    const isReportDataValid = useCallback(() => {
        return Boolean(
            reportType &&
            hasValidSelectedTeam(selectedTeamId) &&
            fromDate &&
            toDate &&
            !startDateError &&
            !endDateError &&
            isRangeValid() &&
            !shouldRestrictGroundTeamRatingsReport
        );
    }, [
        reportType,
        selectedTeamId,
        fromDate,
        toDate,
        startDateError,
        endDateError,
        isRangeValid,
        shouldRestrictGroundTeamRatingsReport,
        hasValidSelectedTeam,
    ]);

    // Handlers
    const onReportTypeChange = useCallback(
        (value: string | undefined) => {
            const reportTypeValue =
                ReportType[value as keyof typeof ReportType];

            setReportType(reportTypeValue);

            if (reportTypeValue === ReportType.GroundTeamRatingsReport) {
                if (canViewAllTeams === false) {
                    setSelectedTeamId(getDefaultGroundTeamId());
                }
                return;
            }

            if (!isConversationReport(reportTypeValue)) {
                setSelectedTeamId('');
            }
        },
        [
            canViewAllTeams,
            getDefaultGroundTeamId,
        ]
    );

    const handleTeamSelect = useCallback((value?: string) => {
        setSelectedTeamId(value ?? getDefaultGroundTeamId());
    }, [getDefaultGroundTeamId]);

    const resetDialogState = useCallback(() => {
        setReportType(undefined);
        setSelectedTeamId("");
        setIncludeCheckIns(false);
        setFromDate(undefined);
        setToDate(undefined);
        setStartDateError(undefined);
        setEndDateError(undefined);
    }, []);

    useEffect(() => {
        if (
            reportType === ReportType.GroundTeamRatingsReport &&
            canViewAllTeams === false
        ) {
            setSelectedTeamId(getDefaultGroundTeamId());
        }
    }, [
        reportType,
        canViewAllTeams,
        getDefaultGroundTeamId,
    ]);

    return (
        <Dialog
            open={open}
            onOpenChange={(
                event: DialogOpenChangeEvent,
                data: DialogOpenChangeData
            ) => {
                resetDialogState();
                if (!!onOpenChange) {
                    onOpenChange(event, data);
                }
            }}
        >
            <DialogSurface>
                <DialogBody>
                    <DialogTitle
                        action={
                            <DialogTrigger action="close">
                                <Button
                                    appearance="subtle"
                                    aria-label="close"
                                    icon={<Dismiss24Regular />}
                                />
                            </DialogTrigger>
                        }
                    >
                        {t('NewReportDialog_Title')}
                    </DialogTitle>
                    <DialogContent>
                        <Field
                            required
                            label={t('NewReportDialog_ReportType')}
                            className={styles.control}
                        >
                            <Dropdown
                                placeholder={t(
                                    'NewReportDialog_ReportTypePlaceholder'
                                )}
                                onOptionSelect={(_, data) =>
                                    onReportTypeChange(data.optionValue)
                                }
                            >
                                {reportTypes.map((key) => {
                                    const label = t(`ReportType_${key}`) ?? '';
                                    const isDisabled = isRestrictedOption(key);

                                    return (
                                        <Option
                                            key={`option_${key}`}
                                            text={label}
                                            value={key}
                                            disabled={isDisabled}
                                        >
                                            {label}
                                        </Option>
                                    );
                                })}
                            </Dropdown>
                        </Field>

                        {shouldRestrictGroundTeamRatingsReport && (
                            <div className={styles.errorMessage}>
                                {t('NewReportDialog_RatingsReportTeamError')}
                            </div>

                        )}

                        {acceptsTeamFilter(reportType) && (
                            <Field
                                required
                                label={t('NewReportDialog_ReportGroundTeam')}
                                className={styles.control}
                            >
                                <Dropdown
                                    value={selectedTeamName}
                                    selectedOptions={[selectedTeamId]}
                                    disabled={isTeamSelectionLocked}
                                    placeholder={
                                        t(
                                            'NewReportDialog_SelectTeamPlaceholder'
                                        )!
                                    }
                                    onOptionSelect={(_, data) => handleTeamSelect(data.optionValue)}
                                >
                                    {teamsWithAll.map((team) => (
                                        <Option
                                            key={team.id}
                                            value={team.id}
                                            text={team.name}
                                        >
                                            {team.name}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </Field>
                        )}
                        <Field
                            required
                            label={t('Reporting_StartDate')}
                            validationMessage={
                                startDateError &&
                                defaultDatePickerErrorStrings[startDateError]
                            }
                            className={styles.control}
                        >
                            <DatePicker
                                maxDate={fromMaxDate}
                                formatDate={onFormatDate}
                                placeholder="Select a report start date"
                                allowTextInput
                                onChange={(_, data) => {
                                    setFromDate(new Date(data.value));
                                }}
                                onSelectDate={(date) => {
                                    setFromDate(date);
                                }}
                                onValidationResult={(data) =>
                                    setStartDateError(data.error)
                                }
                                disabled={isFormDisabled}
                            />
                        </Field>
                        <Field
                            required
                            label={t('Reporting_EndDate')}
                            validationMessage={
                                endDateError &&
                                defaultDatePickerErrorStrings[endDateError]
                            }
                            className={styles.control}
                        >
                            <DatePicker
                                minDate={fromDate ?? undefined}
                                maxDate={toMaxDate}
                                formatDate={onFormatDate}
                                placeholder="Select a report end date"
                                allowTextInput
                                onChange={(_, data) => {
                                    setToDate(addDays(new Date(data.value), 1));
                                }}
                                onSelectDate={(date) => {
                                    if (date) {
                                        date = addDays(date, 1);
                                    }
                                    setToDate(date);
                                }}
                                onValidationResult={(data) =>
                                    setEndDateError(data.error)
                                }
                                disabled={isFormDisabled}
                            />
                        </Field>
                        {fromDate && toDate && !isRangeValid() && (
                            <div className={styles.errorMessage}>
                                {t(
                                    'NewReportDialog_ReportDateFilterError'
                                ).replace(
                                    '{months}',
                                    String(Math.floor(maxRangeDays / 30))
                                )}
                            </div>
                        )}
                        {isConversationReport(reportType) && (
                            <Field className={styles.control}>
                                <Checkbox
                                    checked={includeCheckIns}
                                    onChange={(_, data) =>
                                        setIncludeCheckIns(!!data.checked)
                                    }
                                    label={t('NewReportDialog_IncludeCheckIns')}
                                />
                                {includeCheckIns && (
                                    <div className={styles.warningMessage}>
                                        {selectedTeamId ===
                                            allTeamsOption.id
                                            ? t(
                                                'NewReportDialog_IncludeCheckInsWarning_AllTeams'
                                            )
                                            : t(
                                                'NewReportDialog_IncludeCheckInsWarning_FilteredTeam'
                                            )}
                                    </div>
                                )}
                            </Field>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                disabled={!isReportDataValid()}
                                appearance="primary"
                                onClick={(e) => {
                                    if (!isReportDataValid()) return;

                                    onSubmit(e, {
                                        type: reportType!,
                                        from: fromDate!,
                                        to: toDate!,
                                        teamId: selectedTeamId,
                                        installationTeamId,
                                        includeCheckIns: isConversationReport(
                                            reportType
                                        )
                                            ? includeCheckIns
                                            : undefined,
                                    });
                                }}
                            >
                                {t('NewReportDialog_ActionCreate')}
                            </Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default NewReportDialogAction;